import { useState } from 'react'
import { CONTENT_PAGE_HEADERS } from "~/lib/cache.server"
import type { MetaFunction } from 'react-router'
import { useLoaderData, Link } from 'react-router'
import { db } from '@afficheservices/db'
import { blogPosts } from '@afficheservices/db/schema'
import { eq, desc } from 'drizzle-orm'
import { Navbar } from '~/components/navbar'
import { Footer } from '~/components/footer'
import { StructuredData } from '~/components/structured-data'
import { Clock, ArrowRight, Mail, TrendingUp, BookOpen } from 'lucide-react'

export function headers() { return CONTENT_PAGE_HEADERS }

export const meta: MetaFunction = () => [
  { title: 'Blog — Conseils travaux et maison | AfficheServices' },
  { name: 'description', content: 'Conseils pratiques pour vos travaux : guides, astuces et recommandations par nos experts.' },
  { tagName: 'link', rel: 'canonical', href: 'https://www.afficheservices.fr/blog' },
  { property: 'og:title', content: 'Blog — Conseils travaux' },
  { property: 'og:description', content: 'Conseils pratiques pour vos travaux : guides, astuces et recommandations.' },
  { property: 'og:image', content: 'https://www.afficheservices.fr/og-default.png' },
  { property: 'og:url', content: 'https://www.afficheservices.fr/blog' },
]

// Fallback articles if DB is empty
const FALLBACK_ARTICLES = [
  { slug: 'comment-choisir-son-plombier', title: 'Comment choisir un bon plombier ? 7 critères essentiels', excerpt: 'Trouvez un plombier de confiance grâce à nos 7 critères clés.', image: 'https://images.unsplash.com/photo-1585704032915-c3400ca199e7?w=600&q=80', category: 'Conseils', date: '15 mars 2026', readTime: '5 min' },
  { slug: 'renovation-salle-de-bain-guide-complet', title: 'Rénovation salle de bain : le guide complet 2026', excerpt: 'De la planification au choix des matériaux, tout pour réussir votre rénovation.', image: 'https://images.unsplash.com/photo-1552321554-5fefe8c9ef14?w=600&q=80', category: 'Guides', date: '12 mars 2026', readTime: '8 min' },
  { slug: 'economiser-chauffage-hiver', title: '10 astuces pour réduire sa facture de chauffage', excerpt: 'Des gestes simples aux travaux d\'isolation pour faire des économies.', image: 'https://images.unsplash.com/photo-1513694203232-719a280e022f?w=600&q=80', category: 'Économies', date: '8 mars 2026', readTime: '6 min' },
  { slug: 'prix-travaux-peinture-2026', title: 'Prix des travaux de peinture en 2026 : tarifs et conseils', excerpt: 'Les tarifs moyens au m² et nos conseils pour un bon rapport qualité-prix.', image: 'https://images.unsplash.com/photo-1562259949-e8e7689d7828?w=600&q=80', category: 'Prix', date: '5 mars 2026', readTime: '4 min' },
  { slug: 'mise-aux-normes-electriques', title: 'Mise aux normes électriques : obligations et coûts', excerpt: 'La norme NF C 15-100 est-elle obligatoire ? Quels sont les coûts ?', image: 'https://images.unsplash.com/photo-1621905252507-b35492cc74b4?w=600&q=80', category: 'Réglementation', date: '1 mars 2026', readTime: '7 min' },
  { slug: 'entretien-toiture-printemps', title: 'Entretien de toiture au printemps : check-list complète', excerpt: 'Le printemps est le moment idéal pour inspecter et entretenir votre toiture.', image: 'https://images.unsplash.com/photo-1600596542815-ffad4c1539a9?w=600&q=80', category: 'Entretien', date: '25 février 2026', readTime: '5 min' },
]

export async function loader() {
  const dbArticles = await db
    .select({
      slug: blogPosts.slug,
      title: blogPosts.titleFr,
      excerpt: blogPosts.excerptFr,
      image: blogPosts.coverImageUrl,
      tags: blogPosts.tags,
      publishedAt: blogPosts.publishedAt,
      createdAt: blogPosts.createdAt,
    })
    .from(blogPosts)
    .where(eq(blogPosts.status, 'published'))
    .orderBy(desc(blogPosts.publishedAt))
    .limit(60)

  const articles = dbArticles.length > 0
    ? dbArticles.map((a) => ({
        slug: a.slug,
        title: a.title,
        excerpt: a.excerpt ?? '',
        image: a.image ?? 'https://images.unsplash.com/photo-1600585154340-be6161a56a0c?w=600&q=80',
        category: a.tags?.[0] ?? 'Conseils',
        date: (a.publishedAt ?? a.createdAt).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' }),
        readTime: `${Math.max(3, Math.round((a.excerpt?.length ?? 100) / 30))} min`,
        publishedAtIso: (a.publishedAt ?? a.createdAt).toISOString(),
      }))
    : FALLBACK_ARTICLES.map((a) => ({ ...a, publishedAtIso: new Date().toISOString() }))

  return { articles }
}

export default function BlogIndex() {
  const { articles } = useLoaderData<typeof loader>()
  const [activeTag, setActiveTag] = useState<string | null>(null)
  const [showAllTags, setShowAllTags] = useState(false)

  // Count articles per tag and sort by frequency (most articles first).
  // Limited to top 5 by default — full list felt like a chaotic word cloud.
  const tagCounts = articles.reduce<Record<string, number>>((acc, a) => {
    acc[a.category] = (acc[a.category] ?? 0) + 1
    return acc
  }, {})
  const sortedTags = Object.entries(tagCounts).sort((a, b) => b[1] - a[1])
  const visibleTags = showAllTags ? sortedTags : sortedTags.slice(0, 5)
  const hiddenCount = sortedTags.length - visibleTags.length
  const filtered = activeTag ? articles.filter((a) => a.category === activeTag) : articles

  return (
    <div className="min-h-screen bg-surface-secondary">
      <Navbar />
      <StructuredData data={{
        '@context': 'https://schema.org',
        '@type': 'Blog',
        '@id': 'https://www.afficheservices.fr/blog#blog',
        name: 'Blog AfficheServices',
        url: 'https://www.afficheservices.fr/blog',
        description: 'Conseils, guides et actualités sur la rénovation, les artisans et les travaux en France.',
        inLanguage: 'fr-FR',
        publisher: { '@id': 'https://www.afficheservices.fr/#organization' },
        blogPost: articles.slice(0, 20).map((a) => ({
          '@type': 'BlogPosting',
          headline: a.title,
          url: `https://www.afficheservices.fr/blog/${a.slug}`,
          image: a.image,
          datePublished: a.publishedAtIso,
          author: { '@type': 'Organization', name: 'AfficheServices' },
          publisher: { '@id': 'https://www.afficheservices.fr/#organization' },
          articleSection: a.category,
        })),
      }} />
      <div className="mx-auto max-w-7xl px-4 py-12 lg:px-6">
        <div className="text-center">
          <h1 className="text-3xl font-bold text-slate-900">Blog & Conseils</h1>
          <p className="mt-3 text-slate-500">Guides pratiques, astuces et conseils d'experts pour vos travaux</p>

          {/* Tag filters — top 5 by article count, expand for the rest */}
          {sortedTags.length > 1 && (
            <div className="mt-6 flex flex-wrap justify-center gap-2">
              <button
                type="button"
                onClick={() => setActiveTag(null)}
                className={`rounded-full px-4 py-1.5 text-xs font-medium transition-all ${
                  activeTag === null ? 'bg-primary text-white' : 'bg-white border border-slate-200 text-slate-600 hover:border-primary hover:text-primary'
                }`}
              >
                Tous <span className="opacity-60">({articles.length})</span>
              </button>
              {visibleTags.map(([tag, count]) => (
                <button
                  key={tag}
                  type="button"
                  onClick={() => setActiveTag(activeTag === tag ? null : tag)}
                  className={`rounded-full px-4 py-1.5 text-xs font-medium transition-all ${
                    activeTag === tag ? 'bg-primary text-white' : 'bg-white border border-slate-200 text-slate-600 hover:border-primary hover:text-primary'
                  }`}
                >
                  {tag} <span className="opacity-60">({count})</span>
                </button>
              ))}
              {hiddenCount > 0 && (
                <button
                  type="button"
                  onClick={() => setShowAllTags(true)}
                  className="rounded-full border border-dashed border-slate-300 px-4 py-1.5 text-xs font-medium text-slate-500 hover:border-primary hover:text-primary"
                >
                  + {hiddenCount} autres
                </button>
              )}
              {showAllTags && sortedTags.length > 5 && (
                <button
                  type="button"
                  onClick={() => setShowAllTags(false)}
                  className="rounded-full border border-dashed border-slate-300 px-4 py-1.5 text-xs font-medium text-slate-500 hover:border-primary hover:text-primary"
                >
                  Réduire
                </button>
              )}
            </div>
          )}
        </div>

        {/* Featured */}
        {filtered.length > 0 && (
          <div className="mt-10">
            <Link to={`/blog/${filtered[0].slug}`} className="card-hover group grid overflow-hidden rounded-2xl border border-border bg-white shadow-xs md:grid-cols-2">
              <div className="h-64 overflow-hidden md:h-auto">
                <img src={filtered[0].image} alt={filtered[0].title} className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-105" loading="eager" onError={(e) => { const i = e.currentTarget; if (!i.src.includes('photo-1600585154340')) i.src = 'https://images.unsplash.com/photo-1600585154340-be6161a56a0c?w=800' }} />
              </div>
              <div className="flex flex-col justify-center p-8">
                <span className="w-fit rounded-full bg-primary-50 px-3 py-1 text-xs font-semibold text-primary">{filtered[0].category}</span>
                <h2 className="mt-4 text-2xl font-bold text-slate-900 group-hover:text-primary">{filtered[0].title}</h2>
                <p className="mt-3 text-sm leading-relaxed text-slate-500">{filtered[0].excerpt}</p>
                <div className="mt-4 flex items-center gap-3 text-xs text-slate-400">
                  <span>{filtered[0].date}</span><span>·</span>
                  <div className="flex items-center gap-1"><Clock className="h-3 w-3" /> {filtered[0].readTime}</div>
                </div>
              </div>
            </Link>
          </div>
        )}

        {/* First row of articles */}
        <div className="mt-10 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {filtered.slice(1, 7).map((article) => (
            <Link key={article.slug} to={`/blog/${article.slug}`} className="card-hover group overflow-hidden rounded-2xl border border-border bg-white shadow-xs">
              <div className="aspect-video overflow-hidden">
                <img src={article.image} alt={article.title} className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-105" loading="lazy" onError={(e) => { const i = e.currentTarget; if (!i.src.includes('photo-1600585154340')) i.src = 'https://images.unsplash.com/photo-1600585154340-be6161a56a0c?w=800' }} />
              </div>
              <div className="p-5">
                <span className="rounded-full bg-slate-100 px-2.5 py-0.5 text-xs font-medium text-slate-600">{article.category}</span>
                <h3 className="mt-3 text-base font-semibold text-slate-900 group-hover:text-primary">{article.title}</h3>
                <p className="mt-2 line-clamp-2 text-sm text-slate-500">{article.excerpt}</p>
                <div className="mt-3 flex items-center gap-3 text-xs text-slate-400">
                  <span>{article.date}</span><span>·</span>
                  <div className="flex items-center gap-1"><Clock className="h-3 w-3" /> {article.readTime}</div>
                </div>
              </div>
            </Link>
          ))}
        </div>

        {/* Newsletter CTA */}
        <div className="mt-10 rounded-2xl bg-gradient-to-r from-primary to-blue-600 p-8 text-white sm:flex sm:items-center sm:justify-between">
          <div>
            <h3 className="flex items-center gap-2 text-lg font-bold"><Mail className="h-5 w-5" /> Restez informé</h3>
            <p className="mt-1 text-sm text-blue-200">Recevez nos meilleurs conseils travaux chaque semaine.</p>
          </div>
          <form
            onSubmit={(e) => { e.preventDefault(); const input = e.currentTarget.querySelector('input'); if (input) { input.value = ''; input.placeholder = 'Merci !' } }}
            className="mt-4 flex gap-2 sm:mt-0"
          >
            <input type="email" required placeholder="votre@email.fr"
              className="min-w-0 flex-1 rounded-xl border-0 bg-white/20 px-4 py-2.5 text-sm text-white placeholder:text-blue-200 focus:bg-white/30 focus:outline-none focus:ring-2 focus:ring-white/50" />
            <button type="submit" className="shrink-0 rounded-xl bg-white px-5 py-2.5 text-sm font-semibold text-primary hover:bg-blue-50">
              S'abonner
            </button>
          </form>
        </div>

        {/* Remaining articles */}
        {filtered.length > 7 && (
          <div className="mt-10 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {filtered.slice(7).map((article) => (
              <Link key={article.slug} to={`/blog/${article.slug}`} className="card-hover group overflow-hidden rounded-2xl border border-border bg-white shadow-xs">
                <div className="aspect-video overflow-hidden">
                  <img src={article.image} alt={article.title} className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-105" loading="lazy" onError={(e) => { const i = e.currentTarget; if (!i.src.includes('photo-1600585154340')) i.src = 'https://images.unsplash.com/photo-1600585154340-be6161a56a0c?w=800' }} />
                </div>
                <div className="p-5">
                  <span className="rounded-full bg-slate-100 px-2.5 py-0.5 text-xs font-medium text-slate-600">{article.category}</span>
                  <h3 className="mt-3 text-base font-semibold text-slate-900 group-hover:text-primary">{article.title}</h3>
                  <p className="mt-2 line-clamp-2 text-sm text-slate-500">{article.excerpt}</p>
                  <div className="mt-3 flex items-center gap-3 text-xs text-slate-400">
                    <span>{article.date}</span><span>·</span>
                    <div className="flex items-center gap-1"><Clock className="h-3 w-3" /> {article.readTime}</div>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        )}

        {/* Devis CTA */}
        <div className="mt-10 rounded-2xl border-2 border-dashed border-primary/30 bg-primary/5 p-8 text-center">
          <BookOpen className="mx-auto h-8 w-8 text-primary" />
          <h3 className="mt-3 text-lg font-bold text-slate-900">Besoin d'un artisan pour votre projet ?</h3>
          <p className="mt-1 text-sm text-slate-500">Recevez 3 devis gratuits d'artisans vérifiés sous 48h</p>
          <Link to="/demande-devis/1" className="mt-4 inline-flex items-center gap-2 rounded-xl bg-primary px-6 py-3 text-sm font-semibold text-white shadow-sm hover:bg-primary-hover btn-press">
            Demander un devis gratuit <ArrowRight className="h-4 w-4" />
          </Link>
        </div>

        {/* Article count */}
        <p className="mt-6 text-center text-xs text-slate-400">
          <TrendingUp className="mr-1 inline h-3 w-3" /> {filtered.length} articles disponibles
        </p>
      </div>
      <Footer />
    </div>
  )
}
